import { useEffect, useState, lazy, Suspense } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/contexts/AuthContext";
import { StudentAIProvider, useStudentAI } from "@/contexts/StudentAIContext";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Users, Dumbbell, ClipboardList, LogOut, Brain, Settings, PanelLeftClose, PanelLeft, LayoutTemplate, UserPlus, BarChart3, DollarSign, Apple, Sparkles, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import ProfessorOnboardingTour from "@/components/ProfessorOnboardingTour";

const EmbeddedKyraChat = lazy(() => import("@/components/EmbeddedKyraChat"));

const baseNavItems = [
  { label: "Alunos", path: "/", icon: Users },
  { label: "Exercícios", path: "/exercicios", icon: Dumbbell },
  { label: "Templates", path: "/templates", icon: LayoutTemplate },
  { label: "Anamneses", path: "/admin/anamneses", icon: ClipboardList },
  { label: "Configurações", path: "/configuracoes", icon: Settings },
];

function ProfessorLayoutInner() {
  const { studentData } = useStudentAI();
  const { signOut, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, isPlatformOwner, loading: tenantLoading, membership } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const isMaster = membership?.role === "master" || membership?.role === "owner";
  
  const isNutricionista = role === "nutricionista";

  const nutriNavItems = [
    { label: "Alunos", path: "/nutricao-alunos", icon: Users },
    { label: "Revisão", path: "/nutricao-revisao", icon: Apple },
    { label: "Base de Conhecimento", path: "/base-conhecimento-nutricao", icon: ClipboardList },
    { label: "Configurações", path: "/nutricao-config-nutri", icon: Settings },
  ];

  const navItems = isNutricionista
    ? nutriNavItems
    : [
        ...baseNavItems,
        ...(isMaster ? [
          { label: "Nutrição", path: "/nutricao-revisao", icon: Apple },
          { label: "Config Nutrição", path: "/nutricao-config", icon: Apple },
          { label: "Financeiro", path: "/financeiro", icon: DollarSign },
          { label: "Métricas", path: "/metricas", icon: BarChart3 },
          { label: "Equipe", path: "/equipe", icon: UserPlus },
        ] : []),
      ];
  
  const roleLabel = isNutricionista ? "Nutricionista" : role === "master" ? "Master" : "Professor";
  const [tenant, setTenant] = useState<{ logo_url: string | null; brand_color_hsl: string | null; name: string } | null>(null);

  useEffect(() => {
    if (!tenantLoading && isPlatformOwner && !tenantId) {
      navigate("/admin", { replace: true });
    }
  }, [tenantLoading, isPlatformOwner, tenantId, navigate]);

  useEffect(() => {
    if (!tenantId) return;
    supabase
      .from("tenants")
      .select("logo_url, brand_color_hsl, name")
      .eq("id", tenantId)
      .single()
      .then(({ data }) => {
        if (data) setTenant(data as any);
      });
  }, [tenantId]);

  useEffect(() => {
    if (tenant?.brand_color_hsl) {
      document.documentElement.style.setProperty("--primary", tenant.brand_color_hsl);
    }
    // Apply professor PWA manifest with tenant branding
    if (tenant?.name) {
      import("@/lib/dynamicManifest").then(({ applyProfessorManifest }) => {
        applyProfessorManifest(tenant.name, tenant.logo_url, tenant.brand_color_hsl);
      });
    }
    return () => {
      document.documentElement.style.removeProperty("--primary");
    };
  }, [tenant?.brand_color_hsl, tenant?.name, tenant?.logo_url]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar — desktop */}
      <aside
        role="navigation"
        aria-label="Menu principal"
        className={cn(
          "hidden md:flex flex-col border-r border-border/10 bg-[hsl(var(--sidebar-background))] sticky top-0 h-screen transition-all duration-200 z-30",
          collapsed ? "w-[56px]" : "w-[210px]"
        )}
      >
        {/* Brand */}
        <div className="p-3.5 flex items-center gap-3 min-h-[60px]">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0 rounded-xl" aria-label="Ir para página inicial">
            {tenant?.logo_url ? (
              <div className="h-9 w-9 rounded-xl overflow-hidden bg-muted/20 flex items-center justify-center shrink-0">
                <img src={tenant.logo_url} alt={tenant.name} className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="h-9 w-9 rounded-xl bg-primary/8 flex items-center justify-center shrink-0">
                <Brain className="h-4.5 w-4.5 text-primary" />
              </div>
            )}
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="text-[13px] font-bold text-foreground leading-tight truncate">
                  {isNutricionista ? "Nutricionista Camila" : (tenant?.name || "PersonAI")}
                </h1>
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] mt-0.5">{roleLabel}</p>
              </div>
            )}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 px-2 space-y-0.5">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] transition-all duration-200 group relative",
                  active
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                )}
              >
                {active && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-primary" />
                )}
                <item.icon className={cn("h-[17px] w-[17px] shrink-0 transition-colors", active ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Collapse + logout */}
        <div className="p-2 border-t border-border/15 space-y-0.5">
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            {collapsed ? <PanelLeft className="h-4 w-4 shrink-0" aria-hidden="true" /> : <PanelLeftClose className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {!collapsed && <span>Recolher</span>}
          </button>
          <button
            onClick={signOut}
            aria-label="Sair da conta"
            className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/8 transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="border-b border-border/10 bg-background/90 backdrop-blur-xl sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 sm:px-6 h-[52px]">
            {/* Mobile brand */}
            <div className="md:hidden flex items-center gap-2">
              <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 rounded-lg" aria-label="Ir para página inicial">
                {tenant?.logo_url ? (
                  <div className="h-8 w-8 rounded-lg overflow-hidden bg-muted/20">
                    <img src={tenant.logo_url} alt={tenant.name} className="h-full w-full object-contain" />
                  </div>
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-primary/8 flex items-center justify-center">
                    <Brain className="h-4 w-4 text-primary" />
                  </div>
                )}
                <span className="text-[13px] font-semibold text-foreground">{isNutricionista ? "Nutricionista Camila" : (tenant?.name || "PersonAI")}</span>
              </button>
            </div>

            <div className="hidden md:block" />

            {/* Actions */}
            <div className="flex items-center gap-1">
              <NotificationBell />
            </div>
          </div>

          {/* Mobile nav */}
          <div className="md:hidden border-t border-border/10 px-2">
            <nav className="flex items-center gap-0.5 overflow-x-auto py-1 scrollbar-none" aria-label="Menu de navegação mobile">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "h-8 px-2.5 text-[11px] gap-1.5 whitespace-nowrap shrink-0 rounded-lg",
                    isActive(item.path)
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground"
                  )}
                >
                  <item.icon className="h-3.5 w-3.5" />
                  {item.label}
                </Button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={signOut}
                className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive shrink-0"
              >
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </nav>
          </div>
        </header>

        <main id="main-content">
          <Outlet />
        </main>
        <ProfessorOnboardingTour />
      </div>

      {/* Floating AI Button */}
      <button
        onClick={() => setAiChatOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group md:bottom-8 md:right-8"
        aria-label="Abrir assistente IA"
      >
        <Sparkles className="h-5 w-5 group-hover:rotate-12 transition-transform" />
      </button>

      {/* AI Chat Sheet */}
      <Sheet open={aiChatOpen} onOpenChange={setAiChatOpen}>
        <SheetContent side="right" className="w-full sm:w-[440px] sm:max-w-[440px] p-0 flex flex-col">
          <SheetHeader className="px-4 pt-4 pb-2 border-b border-border/30">
            <SheetTitle className="flex items-center gap-2 text-base">
              <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
              </div>
              PersonAI {studentData ? `— ${studentData.name}` : ""}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 px-3 py-3">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}>
              {aiChatOpen && <EmbeddedKyraChat studentContext={studentData || undefined} studentId={studentData?.id} maxHeight="calc(100vh - 160px)" className="h-full" />}
            </Suspense>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function ProfessorLayout() {
  return (
    <StudentAIProvider>
      <ProfessorLayoutInner />
    </StudentAIProvider>
  );
}
